import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Money from '../Money';
import { withRouter } from 'react-router-dom';
import { H2 } from '@hobs/components/Headings';
import { Formik, Form, Field } from 'formik';
import Textarea from '@hobs/components/Textarea';
import Input from '@hobs/components/Input';
import { GhostButton } from '@hobs/components/Buttons';
import Switch from '@hobs/components/Switch';
import Dropdown from '@hobs/components/Dropdown';
import { MAX_INT32 } from '@hobs/utils/constants';
import * as Yup from 'yup';
import Confirm from '../Confirm';
import cx from 'classnames';
import ShortId from 'shortid';

import './CancellationCosts.scss';
import Dialog from '../Dialog';
import Icon from '../Icon';
import DropZone from '../DropZone';

const CancellationCosts = ({
  t,
  cancellation,
  isEditable,
  valueAddedTaxes,
  valueAddedTaxId,
  getSelectedVat,
  onSubmit,
  isPreviewMode,
  finalizeCancellationCosts,
  exerciseCurrency,
  isCancellationFileDialogOpen,
  onCancellationFileDialogOpen,
  onCancellationFileDialogClose,
  onFileSelect
}) => {
  const schema = Yup.object().shape({
    cancellationCostInvolved: Yup.boolean().required(),
    isCancellationFinalized: Yup.boolean().required(),
    quantity: Yup.number()
      .required()
      .min(0, 'Quantity must be 0')
      .max(0, 'Quantity must be 0'),
    price: Yup.number()
      .required()
      .min(0, 'Unit price must be 0')
      .max(0, 'Unit price must be 0'),
    totalCost: Yup.number()
      .required('Total cost is required')
      .min(0, 'Total cost must be greater than or equal to 0')
      .when('cancellationCostInvolved', {
        is: true,
        then: Yup.number().positive('Total cost must be greater than 0')
      }),
    totalCostDisplay: Yup.number()
      .required('Total cost is required')
      .min(0, 'Total cost must be greater than or equal to 0')
      .when('cancellationCostInvolved', {
        is: true,
        then: Yup.number()
          .positive('Total cost must be greater than 0')
          .max(MAX_INT32, 'Number is too large')
      }),
    valueAddedTaxId: Yup.string().when('cancellationCostInvolved', {
      is: true,
      then: Yup.string().required('VAT is required')
    }),
    comment: Yup.string()
      .max(500, t('Comment cannot be longer than 500 symbols'))
      .required(t('Comment is required'))
  });

  function submitForm(values, { setSubmitting, setErrors }) {
    onSubmit(values, { setSubmitting, setErrors });
  }

  function updateIsCostFinalized(isCancellationFinalized, setFieldValue) {
    finalizeCancellationCosts(() => {
      setFinalCostValue(isCancellationFinalized, setFieldValue);
    });
  }

  function setFinalCostValue(isCancellationFinalized, setFieldValue) {
    setFieldValue('isCancellationFinalized', isCancellationFinalized);
  }

  function setCancellationCostState(cancellationCostInvolved, setFieldValue) {
    setFieldValue('cancellationCostInvolved', cancellationCostInvolved);
    if (!cancellationCostInvolved) {
      setFieldValue('totalCost', 0);
      setFieldValue('totalCostDisplay', parseFloat(0).toFixed(2));
      setFieldValue('grossAmount', 0);
      setFieldValue('valueAddedTaxId', valueAddedTaxId);
    }
  }

  function calculateCosts(totalCost, valueAddedTaxId, setFieldValue) {
    setFieldValue('totalCost', totalCost);
    setFieldValue('totalCostDisplay', parseFloat(totalCost).toFixed(2));
    setFieldValue(
      'grossAmount',
      calculateGrossAmount(totalCost, valueAddedTaxId).toFixed(2)
    );
  }

  function calculateGrossAmount(totalCost, valueAddedTaxId) {
    return totalCost * (getSelectedVat(valueAddedTaxId).percentage / 100 + 1);
  }

  function resetCancellationCostErrors(errors) {
    Object.keys(errors).forEach(key =>
      key.toLowerCase() !== 'comment' ? delete errors[key] : errors[key]
    );
  }

  if (!isEditable) {
    return (
      <div class="cancellation-costs">
        <H2>{t('Cancellation Costs')}</H2>
        <table className="table">
          <thead>
            <tr>
              <th>{t('Q-ty')}</th>
              <th>{t('Currency')}</th>
              <th>{t('Price')}</th>
              <th>{t('Total')}</th>
              <th>{t('VAT')}</th>
              <th>{t('Gross amount')}</th>
              <th>{t('Comment')}</th>
              <th>{t('Files')}</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="small">{cancellation.quantity}</td>
              <td className="small">{exerciseCurrency}</td>
              <td className="small">
                <Money amount={cancellation.unitCost} />
              </td>
              <td className="small">
                <Money amount={cancellation.totalCost} />
              </td>
              <td className="medium">{cancellation.vatValue}%</td>
              <td className="small">
                <Money amount={cancellation.grossAmount} />
              </td>
              <td className="comment">{cancellation.comment}</td>
              <td className="small">
                <div
                  className="response-attachment-link"
                  onClick={() => onCancellationFileDialogOpen()}
                >
                  <Icon
                    icon="attachment"
                    className={cx('files-icon', {
                      'no-files': !cancellation.attachments.length
                    })}
                  />
                  {cancellation.attachments.length
                    ? `(${cancellation.attachments.length})`
                    : ''}
                </div>
                <Dialog
                  title={t('Cancellation costs files')}
                  isOpen={isCancellationFileDialogOpen}
                  onClose={onCancellationFileDialogClose}
                  className="response-files"
                >
                  <DropZone
                    value={cancellation.attachments}
                    key={ShortId.generate()}
                    disabled={true}
                    showSuccessMessages={true}
                  />
                </Dialog>
              </td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div class="cancellation-costs">
      <H2>{t('Cancellation Costs')}</H2>
      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={schema}
        enableReinitialize={true}
        initialValues={{
          cancellationCostInvolved: cancellation.totalCost > 0,
          isCancellationFinalized: cancellation.isFinalized,
          quantity: cancellation.quantity,
          comment: cancellation.comment,
          price: cancellation.unitCost,
          priceDisplay: cancellation.unitCost.toFixed(2),
          totalCostDisplay: cancellation.totalCost.toFixed(2),
          totalCost: cancellation.totalCost,
          grossAmount: cancellation.grossAmount,
          valueAddedTaxId: valueAddedTaxId,
          attachments: cancellation.attachments || []
        }}
        onSubmit={submitForm}
      >
        {({ setFieldValue, values, errors, isSubmitting }) => (
          <Form>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('Q-ty')}</th>
                  <th>{t('Currency')}</th>
                  <th>{t('Price')}</th>
                  <th>{t('Total')}</th>
                  <th>{t('VAT')}</th>
                  <th>{t('Gross amount')}</th>
                  <th>{t('Comment')}</th>
                  <th>{t('Files')}</th>
                  <th></th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="small">
                    <Field
                      name="quantity"
                      component={Input}
                      noMargin
                      min={0}
                      type="number"
                      disabled={true}
                    ></Field>
                    {errors.Quantity && (
                      <div className="error-field">{errors.Quantity}</div>
                    )}
                  </td>
                  <td className="small">
                    <Input
                      name="currencyName"
                      component={Input}
                      noMargin
                      type="text"
                      disabled={true}
                      value={exerciseCurrency}
                    ></Input>
                  </td>
                  <td className="small">
                    <Field
                      name="priceDisplay"
                      component={Input}
                      noMargin
                      min={0}
                      type="number"
                      disabled={true}
                    ></Field>
                    <Field name="price" min={0} type="hidden" />
                    {errors.Price && (
                      <div className="error-field">{errors.Price}</div>
                    )}
                  </td>
                  <td className="small">
                    <Field
                      name="totalCostDisplay"
                      component={Input}
                      type={'number'}
                      noMargin
                      setFieldValue={setFieldValue}
                      min={0}
                      onChange={event => {
                        if (event.target.value > MAX_INT32) {
                          event.target.value = MAX_INT32;
                        }
                        calculateCosts(
                          event.target.value,
                          values.valueAddedTaxId,
                          setFieldValue
                        );
                      }}
                      disabled={
                        !values.cancellationCostInvolved || isPreviewMode
                      }
                    ></Field>
                    <Field name="totalCost" min={0} type="hidden" />
                    {errors.TotalCost && (
                      <div className="error-field">{errors.TotalCost}</div>
                    )}
                  </td>
                  <td className="medium">
                    <div className="vat-select">
                      <Field
                        noMargin
                        setFieldValue={setFieldValue}
                        component={Dropdown}
                        name="valueAddedTaxId"
                        disabled={
                          !values.cancellationCostInvolved || isPreviewMode
                        }
                        onChange={event => {
                          calculateCosts(
                            values.totalCost,
                            event.target.value,
                            setFieldValue
                          );
                        }}
                      >
                        {valueAddedTaxes.map(vat => {
                          return (
                            <option
                              key={vat.valueAddedTaxId}
                              value={vat.valueAddedTaxId}
                              selected={
                                vat.valueAddedTaxId == values.valueAddedTaxId
                              }
                            >
                              {vat.codeAndPercentage}
                            </option>
                          );
                        })}
                      </Field>
                      {errors.ValueAddedTaxId && (
                        <div className="error-field">
                          {errors.ValueAddedTaxId}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="small">
                    <div className="non-editable-field">
                      <span className="small">
                        <Money amount={values.grossAmount} />
                      </span>
                    </div>
                  </td>
                  <td className="comment">
                    <Field
                      name="comment"
                      component={Textarea}
                      noMargin
                      setFieldValue={setFieldValue}
                      rows={1}
                      disabled={isPreviewMode}
                    />
                    {errors.Comment && (
                      <div className="error-field">{errors.Comment}</div>
                    )}
                  </td>
                  <td className="small">
                    <div
                      className="response-attachment-link"
                      onClick={() => onCancellationFileDialogOpen()}
                    >
                      <Icon
                        icon="attachment"
                        className={cx('files-icon', {
                          'no-files': !values.attachments.length
                        })}
                      />
                      {values.attachments.length
                        ? `(${values.attachments.length})`
                        : ''}
                    </div>
                    <Dialog
                      title={t('Cancellation costs files')}
                      isOpen={isCancellationFileDialogOpen}
                      onClose={onCancellationFileDialogClose}
                      className="response-files"
                    >
                      <DropZone
                        onSelect={files => {
                          onFileSelect(files).then(response => {
                            setFieldValue(
                              'attachments',
                              values.attachments.concat(response.payload)
                            );
                          });
                        }}
                        onRemove={(file, list) => {
                          setFieldValue('attachments', list);
                        }}
                        value={values.attachments}
                        key={ShortId.generate()}
                        disabled={!isEditable || isPreviewMode}
                        showSuccessMessages={true}
                      />
                    </Dialog>
                  </td>
                  <td className="medium">
                    <Switch
                      checked={values.cancellationCostInvolved}
                      disabled={isPreviewMode}
                      onChange={cancellationCostInvolved => {
                        let confirmMessage = values.cancellationCostInvolved
                          ? 'Are you sure you want remove cancellation costs?'
                          : 'Are you sure you want add cancellation costs?';
                        Confirm(
                          t('Cancellation costs involved'),
                          t(confirmMessage),
                          () => {
                            setCancellationCostState(
                              cancellationCostInvolved,
                              setFieldValue
                            );
                            if (!cancellationCostInvolved) {
                              resetCancellationCostErrors(errors);
                            }
                          }
                        );
                      }}
                      label={t('Cancellation cost involved')}
                    />
                    <Field name="cancellationCostInvolved" type="hidden" />
                    {errors.CancellationCostInvolved && (
                      <div className="error-field">
                        {errors.CancellationCostInvolved}
                      </div>
                    )}
                  </td>
                  <td>
                    <Switch
                      checked={values.isCancellationFinalized}
                      disabled={isPreviewMode}
                      onChange={isCancellationFinalized => {
                        let confirmMessage = values.isCancellationFinalized
                          ? 'Are you sure you want cancellation costs as not final?'
                          : 'Are you sure you want confirm cancellation costs as final?';
                        Confirm(
                          t('Update final cancellation costs'),
                          t(confirmMessage),
                          () => {
                            updateIsCostFinalized(
                              isCancellationFinalized,
                              setFieldValue
                            );
                          }
                        );
                      }}
                      label={t('Final cancellation costs')}
                    />
                    <Field name="isCancellationFinalized" type="hidden" />
                    {errors.isCancellationFinalized && (
                      <div className="error-field">
                        {errors.isCancellationFinalized}
                      </div>
                    )}
                  </td>
                  {!isPreviewMode && (
                    <td className="controls">
                      <GhostButton
                        type="submit"
                        disabled={isPreviewMode || isSubmitting}
                      >
                        {t('Confirm Costs')}
                      </GhostButton>
                    </td>
                  )}
                  {isPreviewMode && <td></td>}
                </tr>
              </tbody>
            </table>
          </Form>
        )}
      </Formik>
    </div>
  );
};

CancellationCosts.propTypes = {
  cancellation: PropTypes.object,
  t: PropTypes.func,
  isEditable: PropTypes.bool,
  valueAddedTaxes: PropTypes.array,
  valueAddedTaxId: PropTypes.string,
  getSelectedVat: PropTypes.func,
  onSubmit: PropTypes.func,
  isPreviewMode: PropTypes.bool,
  finalizeCancellationCosts: PropTypes.func,
  exerciseCurrency: PropTypes.string,
  onFileSelect: PropTypes.func
};

export default withRouter(withTranslation()(CancellationCosts));
